/**
 * Prerequisite Definitions — Learning Dependency Graph edges.
 *
 * ARCHITECTURE v4.0 — Phase 1, Step 1.6
 *
 * These define the "must master X before Y" relationships between families.
 * Rules:
 * - Prerequisites must have lower or equal tier (tier monotonicity)
 * - Cross-module prerequisites are allowed but should be rare and documented
 * - 'hard' = must master before proceeding; 'soft' = recommended but not blocking
 *
 * STATUS: GET module complete (~80 edges). Other modules need curriculum designer input.
 * GAP 8: Full ~400 edges require curriculum designer collaboration.
 */

import type { PrerequisiteDefinition } from './types'

// ─── GET — Getalbegrip (51 families) ──────────────────────────────────────────

const GET_PREREQUISITES: PrerequisiteDefinition[] = [
  // Skill: positiestelsel — linear progression
  {
    familyId: 'GET-GROOT-LEZEN',
    prerequisites: ['GET-GROOT-POS'],
    notes: 'Must understand place value before reading large numbers',
  },
  {
    familyId: 'GET-GROOT-VERGELIJK',
    prerequisites: ['GET-GROOT-LEZEN'],
    notes: 'Must read numbers before comparing them',
  },
  {
    familyId: 'GET-GROOT-AFRONDEN',
    prerequisites: ['GET-GROOT-VERGELIJK'],
    notes: 'Comparing helps understand rounding',
  },
  {
    familyId: 'GET-ROND-BASIS',
    prerequisites: ['GET-GROOT-AFRONDEN'],
    notes: 'Basic rounding before thousands rounding',
  },
  {
    familyId: 'GET-GROOT-SCHATTEN',
    prerequisites: ['GET-ROND-BASIS'],
    notes: 'Rounding is foundation for estimation',
  },

  // Skill: breuken — branching progression
  {
    familyId: 'GET-BREUK-LEZEN',
    prerequisites: ['GET-BREUK-BASIS'],
    notes: 'Visual understanding before notation',
  },
  {
    familyId: 'GET-BREUK-GEL-PLUS',
    prerequisites: ['GET-BREUK-LEZEN'],
    notes: 'Must read fractions before adding them',
  },
  {
    familyId: 'GET-BREUK-GEL-MIN',
    prerequisites: ['GET-BREUK-GEL-PLUS'],
    notes: 'Addition before subtraction',
  },
  {
    familyId: 'GET-VERGELIJK',
    prerequisites: ['GET-BREUK-LEZEN'],
    notes: 'Must read fractions before comparing',
  },
  {
    familyId: 'GET-BREUK-GETALLENLIJN',
    prerequisites: ['GET-VERGELIJK'],
    notes: 'Comparing helps with number line placement',
  },
  {
    familyId: 'GET-BREUK-VEREENVOUDIG',
    prerequisites: ['GET-VERGELIJK'],
    notes: 'Comparing fractions leads to simplification',
  },
  {
    familyId: 'GET-BREUK-UITBREIDEN',
    prerequisites: ['GET-BREUK-VEREENVOUDIG'],
    notes: 'Simplification and expansion are inverse operations',
  },
  {
    familyId: 'GET-BREUK-ONG-PLUS',
    prerequisites: ['GET-BREUK-GEL-PLUS', 'GET-BREUK-UITBREIDEN'],
    notes: 'Need same-denominator addition + expansion skill',
  },
  {
    familyId: 'GET-BREUK-MIN',
    prerequisites: ['GET-BREUK-ONG-PLUS', 'GET-BREUK-GEL-MIN'],
    notes: 'Unlike subtraction builds on unlike addition',
  },
  {
    familyId: 'GET-BREUK-KEER',
    prerequisites: ['GET-BREUK-VEREENVOUDIG'],
    notes: 'Simplification needed for multiplication results',
  },
  {
    familyId: 'GET-BREUK-GEMENGD',
    prerequisites: ['GET-BREUK-ONG-PLUS'],
    notes: 'Mixed numbers arise from improper fractions in addition',
  },
  {
    familyId: 'GET-BREUK-DEEL',
    prerequisites: ['GET-BREUK-KEER'],
    notes: 'Division = multiply by reciprocal',
  },

  // Skill: kommagetallen — builds on positiestelsel
  {
    familyId: 'GET-KOMMA-WAARDE',
    prerequisites: ['GET-GROOT-POS'],
    notes: 'Place value extends to decimals',
  },
  {
    familyId: 'GET-KOMMA-LEZEN',
    prerequisites: ['GET-KOMMA-WAARDE'],
    notes: 'Understand value before reading/writing',
  },
  {
    familyId: 'GET-KOMMA-VERGELIJK',
    prerequisites: ['GET-KOMMA-LEZEN'],
    notes: 'Must read decimals before comparing',
  },
  {
    familyId: 'GET-KOMMA-GETALLENLIJN',
    prerequisites: ['GET-KOMMA-VERGELIJK'],
    notes: 'Comparing helps with number line placement',
  },
  {
    familyId: 'GET-KOMMA-PLUS',
    prerequisites: ['GET-KOMMA-LEZEN'],
    notes: 'Must read decimals before adding',
  },
  {
    familyId: 'GET-KOMMA-MIN',
    prerequisites: ['GET-KOMMA-PLUS'],
    notes: 'Addition before subtraction',
  },
  {
    familyId: 'GET-KOMMA-KEER',
    prerequisites: ['GET-KOMMA-PLUS'],
    notes: 'Addition understanding before multiplication',
  },
  {
    familyId: 'GET-KOMMA-DEEL',
    prerequisites: ['GET-KOMMA-KEER'],
    notes: 'Multiplication before division',
  },
  {
    familyId: 'GET-DEEL-KOMMA',
    prerequisites: ['GET-KOMMA-DEEL'],
    notes: 'Division of decimals before dividing by decimals',
  },
  {
    familyId: 'GET-KOMMA-AFRONDEN',
    prerequisites: ['GET-KOMMA-VERGELIJK', 'GET-GROOT-AFRONDEN'],
    notes: 'Decimal comparison + rounding concept',
  },

  // Skill: procenten — builds on breuken + kommagetallen
  {
    familyId: 'GET-PROC-BASIS',
    prerequisites: ['GET-BREUK-VEREENVOUDIG', 'GET-KOMMA-WAARDE'],
    notes: 'Percent = fraction = decimal (three-way link)',
  },
  {
    familyId: 'GET-PROC-VAN',
    prerequisites: ['GET-PROC-BASIS'],
    notes: 'Basic percent understanding before calculation',
  },
  {
    familyId: 'GET-VERH-BREUK-PROC-KOPPEL',
    prerequisites: ['GET-PROC-BASIS'],
    notes: 'Deepens the three-way link',
  },
  {
    familyId: 'GET-PROC-NIEUW',
    prerequisites: ['GET-PROC-VAN'],
    notes: 'Calculate percentage before applying discount/markup',
  },
  {
    familyId: 'GET-PROC-SAMENGESTELD',
    prerequisites: ['GET-PROC-VAN'],
    notes: 'Compound percentage builds on basic percentage-of-amount',
  },
  {
    familyId: 'GET-PROC-TOENAME',
    prerequisites: ['GET-PROC-NIEUW'],
    notes: 'Price changes before percentage increase',
  },
  {
    familyId: 'GET-PROC-AFNAME',
    prerequisites: ['GET-PROC-TOENAME'],
    notes: 'Increase before decrease (same pattern)',
  },
  {
    familyId: 'GET-PROC-TERUG',
    prerequisites: ['GET-PROC-NIEUW'],
    notes: 'Forward calculation before reverse',
  },
  {
    familyId: 'GET-PROC-BTW',
    prerequisites: ['GET-PROC-TERUG'],
    notes: 'Reverse calculation is foundation for BTW exclusive',
  },

  // Skill: bewerkingen
  {
    familyId: 'GET-HOOFDREKENEN',
    prerequisites: ['GET-GROOT-POS'],
    notes: 'Place value understanding for mental math',
  },
  {
    familyId: 'GET-SCHATSTRATEGIE',
    prerequisites: ['GET-HOOFDREKENEN', 'GET-ROND-BASIS'],
    notes: 'Mental math + rounding for estimation',
  },
  {
    familyId: 'GET-VOLGORDE-BEWERKING',
    prerequisites: ['GET-KOMMA-KEER'],
    notes: 'Need all four operations before order of operations',
  },

  // Skill: verhoudingen — builds on breuken + procenten
  {
    familyId: 'GET-VERH-RECEPT',
    prerequisites: ['GET-PROC-VAN'],
    notes: 'Percentage of amount is foundation for ratio',
  },
  {
    familyId: 'GET-VERH-SCHAAL',
    prerequisites: ['GET-VERH-RECEPT'],
    notes: 'Recipe ratios before ratio tables',
  },
  {
    familyId: 'GET-VERH-SCHAAL-KAART',
    prerequisites: ['GET-VERH-SCHAAL'],
    notes: 'Ratio tables before map scale',
  },
  {
    familyId: 'GET-VERH-VERDELEN',
    prerequisites: ['GET-VERH-SCHAAL'],
    notes: 'Ratio tables before dividing in ratio',
  },

  // Skill: negatieve_getallen — builds on kommagetallen
  {
    familyId: 'GET-NEGATIEF',
    prerequisites: ['GET-KOMMA-GETALLENLIJN'],
    notes: 'Number line understanding extends to negatives',
  },
  {
    familyId: 'GET-NEGATIEF-PLUS',
    prerequisites: ['GET-NEGATIEF'],
    notes: 'Understand negatives before adding with them',
  },
  {
    familyId: 'GET-NEGATIEF-MIN',
    prerequisites: ['GET-NEGATIEF-PLUS'],
    notes: 'Addition before subtraction with negatives',
  },

  // Skill: machten_wortels — builds on bewerkingen
  {
    familyId: 'GET-MACHT',
    prerequisites: ['GET-VOLGORDE-BEWERKING'],
    notes: 'Order of operations includes exponents',
  },
  {
    familyId: 'GET-MACHT-GRONDTAL',
    prerequisites: ['GET-MACHT'],
    notes: 'Basic exponents before base variations',
  },
  {
    familyId: 'GET-WORTEL',
    prerequisites: ['GET-MACHT'],
    notes: 'Exponents before roots (inverse operation)',
  },

  // Skill: wetenschappelijk — builds on machten
  {
    familyId: 'GET-WETENSCHAPPELIJK',
    prerequisites: ['GET-MACHT', 'GET-KOMMA-KEER'],
    notes: 'Exponents + decimal multiplication for scientific notation',
  },
  {
    familyId: 'GET-WETENSCHAP-REKEN',
    prerequisites: ['GET-WETENSCHAPPELIJK'],
    notes: 'Read notation before calculating with it',
  },
]

// ─── MET — Metriek & Meetkunde (39 families) ─────────────────────────────────
// Starter set — needs curriculum designer review

const MET_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'MET-LEN-SCHATTEN', prerequisites: ['MET-LEN-OMREKEN'] },
  { familyId: 'MET-LEN-NAUWKEURIG', prerequisites: ['MET-LEN-METEN'] },
  { familyId: 'MET-LEN-CONTEXT', prerequisites: ['MET-LEN-OMREKEN'] },
  {
    familyId: 'MET-GEW-OMREKEN',
    prerequisites: ['MET-LEN-OMREKEN'],
    notes: 'Same metric staircase pattern',
  },
  { familyId: 'MET-GEW-SCHATTEN', prerequisites: ['MET-GEW-WEGEN'] },
  { familyId: 'MET-GEW-CONTEXT', prerequisites: ['MET-GEW-OMREKEN'] },
  {
    familyId: 'MET-INH-OMREKEN',
    prerequisites: ['MET-LEN-OMREKEN'],
    notes: 'Same metric staircase pattern',
  },
  {
    familyId: 'MET-INH-BLOK',
    prerequisites: ['MET-OPP-RECHT'],
    notes: 'Area before volume',
  },
  {
    familyId: 'MET-INH-LITER',
    prerequisites: ['MET-INH-OMREKEN', 'MET-INH-BLOK'],
  },
  { familyId: 'MET-INH-OMREKEN-KUBIEK', prerequisites: ['MET-INH-LITER'] },
  { familyId: 'MET-INH-CITER', prerequisites: ['MET-INH-LITER'] },
  { familyId: 'MET-INH-SCHATTEN', prerequisites: ['MET-INH-OMREKEN'] },
  { familyId: 'MET-OMTREK-RECHT', prerequisites: ['MET-LEN-OMREKEN'] },
  { familyId: 'MET-OPP-RECHT', prerequisites: ['MET-LEN-OMREKEN'] },
  { familyId: 'MET-OPP-VIERKANT', prerequisites: ['MET-OPP-RECHT'] },
  { familyId: 'MET-OPP-DRIEHOEK', prerequisites: ['MET-OPP-RECHT'] },
  { familyId: 'MET-OPP-SAMENGESTELD', prerequisites: ['MET-OPP-DRIEHOEK'] },
  { familyId: 'MET-OPP-PARALLELLOGRAM', prerequisites: ['MET-OPP-DRIEHOEK'] },
  { familyId: 'MET-OPP-LAND', prerequisites: ['MET-OPP-RECHT'] },
  { familyId: 'MET-OPP-OMREKENEN', prerequisites: ['MET-OPP-LAND'] },
  { familyId: 'MET-MEETK-DIAMETER', prerequisites: ['MET-OPP-RECHT'] },
  { familyId: 'MET-MEETK-OMTREK', prerequisites: ['MET-MEETK-DIAMETER'] },
  { familyId: 'MET-MEETK-OPP', prerequisites: ['MET-MEETK-OMTREK'] },
  { familyId: 'MET-MEETK-CIRKEL-DEEL', prerequisites: ['MET-MEETK-OPP'] },
  { familyId: 'MET-HOEK-TEKENEN', prerequisites: ['MET-HOEK-METEN'] },
  { familyId: 'MET-HOEK-SOORTEN', prerequisites: ['MET-HOEK-METEN'] },
  { familyId: 'MET-HOEK-BEREKENEN', prerequisites: ['MET-HOEK-SOORTEN'] },
  { familyId: 'MET-DRIEHOEK-SOORTEN', prerequisites: ['MET-HOEK-SOORTEN'] },
  { familyId: 'MET-SCHAAL-BASIS', prerequisites: ['MET-LEN-OMREKEN'] },
  { familyId: 'MET-SCHAAL-LENGTE', prerequisites: ['MET-SCHAAL-BASIS'] },
  {
    familyId: 'MET-SCHAAL-OPP',
    prerequisites: ['MET-SCHAAL-LENGTE', 'MET-OPP-OMREKENEN'],
  },
  { familyId: 'MET-TIJD-NAUWKEURIG', prerequisites: ['MET-TIJD-METEN'] },
]

// ─── TGL — Tijd & Geld (30 families) ─────────────────────────────────────────

const TGL_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'TGL-TIJD-DIGITAAL', prerequisites: ['TGL-TIJD-ANALOOG'] },
  {
    familyId: 'TGL-TIJD-ANALOOG-GEDETAILLEERD',
    prerequisites: ['TGL-TIJD-ANALOOG'],
  },
  {
    familyId: 'TGL-TIJD-CONVERSIE',
    prerequisites: ['TGL-TIJD-DIGITAAL', 'TGL-TIJD-ANALOOG-GEDETAILLEERD'],
  },
  { familyId: 'TGL-TIJD-12-24', prerequisites: ['TGL-TIJD-CONVERSIE'] },
  { familyId: 'TGL-TIJD-TIJDZONES', prerequisites: ['TGL-TIJD-12-24'] },
  { familyId: 'TGL-TIJD-DUUR', prerequisites: ['TGL-TIJD-CONVERSIE'] },
  {
    familyId: 'TGL-TIJD-SAMENGESTELD',
    prerequisites: ['TGL-TIJD-DUUR'],
    notes: 'Compound time calculations build on basic duration',
  },
  { familyId: 'TGL-TIJD-PLANNING', prerequisites: ['TGL-TIJD-DUUR'] },
  { familyId: 'TGL-TIJD-SCHEMA', prerequisites: ['TGL-TIJD-PLANNING'] },
  { familyId: 'TGL-TIJD-DIAGRAM', prerequisites: ['TGL-TIJD-SCHEMA'] },
  { familyId: 'TGL-TIJD-KALENDER', prerequisites: ['TGL-TIJD-DUUR'] },
  { familyId: 'TGL-TIJD-OMREKENEN', prerequisites: ['TGL-TIJD-DUUR'] },
  { familyId: 'TGL-TIJD-SNELHEID', prerequisites: ['TGL-TIJD-OMREKENEN'] },
  { familyId: 'TGL-TIJD-AFSTAND', prerequisites: ['TGL-TIJD-SNELHEID'] },
  { familyId: 'TGL-TIJD-GEMIDDELDE', prerequisites: ['TGL-TIJD-AFSTAND'] },
  { familyId: 'TGL-GELD-TERUG', prerequisites: ['TGL-GELD-MAAK'] },
  { familyId: 'TGL-GELD-WISSEL', prerequisites: ['TGL-GELD-TERUG'] },
  { familyId: 'TGL-GELD-KOMMA', prerequisites: ['TGL-GELD-WISSEL'] },
  { familyId: 'TGL-GELD-TELLEN', prerequisites: [] },
  { familyId: 'TGL-GELD-MAAK', prerequisites: ['TGL-GELD-TELLEN'] },
  { familyId: 'TGL-GELD-WISSELEN', prerequisites: ['TGL-GELD-KOMMA'] },
  { familyId: 'TGL-GELD-BOODSCHAPPEN', prerequisites: ['TGL-GELD-KOMMA'] },
  { familyId: 'TGL-GELD-KOOPJES', prerequisites: ['TGL-GELD-BOODSCHAPPEN'] },
  { familyId: 'TGL-GELD-BUDGET', prerequisites: ['TGL-GELD-KOOPJES'] },
  { familyId: 'TGL-GELD-KORTING', prerequisites: ['TGL-GELD-KOOPJES'] },
  { familyId: 'TGL-GELD-PRIJS-PER', prerequisites: ['TGL-GELD-KOOPJES'] },
  { familyId: 'TGL-GELD-SPAREN', prerequisites: ['TGL-GELD-BUDGET'] },
  { familyId: 'TGL-GELD-RENTE-PROC', prerequisites: ['TGL-GELD-SPAREN'] },
  { familyId: 'TGL-GELD-BTW', prerequisites: ['TGL-GELD-KORTING'] },
  { familyId: 'TGL-GELD-TERUGREKENEN', prerequisites: ['TGL-GELD-BTW'] },
]

// ─── SWW — Spelling Werkwoorden (24 families) ────────────────────────────────

const SWW_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'SWW-TT-MEERVOUD', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-TT-ZIJN-HEBBEN', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-TT-INV-JIJ', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-TT-GEBIEDENDE', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-TT-DSTAM', prerequisites: ['SWW-TT-INV-JIJ'] },
  { familyId: 'SWW-TT-INV-TRICK', prerequisites: ['SWW-TT-DSTAM'] },
  { familyId: 'SWW-VT-STERK', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-VT-ZIJN-HEBBEN', prerequisites: ['SWW-VT-STERK'] },
  { familyId: 'SWW-VT-ZWAK-KOF', prerequisites: ['SWW-TT-STAM'] },
  { familyId: 'SWW-VT-ONREGELMATIG', prerequisites: ['SWW-VT-STERK'] },
  { familyId: 'SWW-VT-VZFS', prerequisites: ['SWW-VT-ZWAK-KOF'] },
  { familyId: 'SWW-VT-ZULLEN-KUNNEN', prerequisites: ['SWW-VT-STERK'] },
  { familyId: 'SWW-VT-DUBBEL', prerequisites: ['SWW-VT-ZWAK-KOF'] },
  { familyId: 'SWW-VD-KOF', prerequisites: ['SWW-VT-ZWAK-KOF'] },
  { familyId: 'SWW-VD-STERK', prerequisites: ['SWW-VT-STERK', 'SWW-VD-KOF'] },
  { familyId: 'SWW-VD-ZONDER-GE', prerequisites: ['SWW-VD-KOF'] },
  { familyId: 'SWW-VD-HULPWW', prerequisites: ['SWW-VD-KOF'] },
  {
    familyId: 'SWW-VD-HOMOFOON',
    prerequisites: ['SWW-VD-KOF', 'SWW-TT-DSTAM'],
  },
  { familyId: 'SWW-BN-VD-KORT', prerequisites: ['SWW-VD-KOF'] },
  { familyId: 'SWW-BN-VD-DUBBEL', prerequisites: ['SWW-BN-VD-KORT'] },
  { familyId: 'SWW-LEEN-ENG-STAM', prerequisites: ['SWW-TT-STAM'] },
  {
    familyId: 'SWW-LEEN-ENG-KOF',
    prerequisites: ['SWW-LEEN-ENG-STAM', 'SWW-VT-ZWAK-KOF'],
  },
  { familyId: 'SWW-LEEN-FRANS', prerequisites: ['SWW-VT-VZFS'] },
]

// ─── SPR — Spelling Regels (20 families) ─────────────────────────────────────

const SPR_PREREQUISITES: PrerequisiteDefinition[] = [
  {
    familyId: 'SPR-KLANK-AA-EE-OO',
    prerequisites: ['SPR-KLANK-JAGER', 'SPR-KLANK-BAKKER'],
  },
  { familyId: 'SPR-KLANK-VERDUBBEL', prerequisites: ['SPR-KLANK-BAKKER'] },
  { familyId: 'SPR-KLANK-VERKORTEN', prerequisites: ['SPR-KLANK-JAGER'] },
  { familyId: 'SPR-KLANK-AU-OU', prerequisites: ['SPR-KLANK-AA-EE-OO'] },
  { familyId: 'SPR-WEET-D-T', prerequisites: ['SPR-WEET-EI-IJ'] },
  { familyId: 'SPR-WEET-DT', prerequisites: ['SPR-WEET-D-T'] },
  { familyId: 'SPR-LEEN-FRANS', prerequisites: ['SPR-LEEN-C-S-K'] },
  { familyId: 'SPR-LEEN-ENGELS', prerequisites: ['SPR-LEEN-C-S-K'] },
  { familyId: 'SPR-LEEN-LATIJN', prerequisites: ['SPR-LEEN-ENGELS'] },
  { familyId: 'SPR-LEEN-GRIEKS', prerequisites: ['SPR-LEEN-LATIJN'] },
  { familyId: 'SPR-SAM-TUSSEN-S', prerequisites: ['SPR-SAM-TUSSEN-N'] },
  { familyId: 'SPR-LEESTEKEN-PUNT-KOMMA', prerequisites: ['SPR-HOOFDLETTER'] },
  { familyId: 'SPR-TREMA-MV', prerequisites: ['SPR-KLANK-AA-EE-OO'] },
  { familyId: 'SPR-ACCENT', prerequisites: ['SPR-LEEN-FRANS'] },
]

// ─── TAA — Taalbegrip (35 families) ──────────────────────────────────────────

const TAA_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'TAA-ONT-GEZEGDE', prerequisites: ['TAA-ONT-BASIS'] },
  { familyId: 'TAA-ONT-VOORZETSEL', prerequisites: ['TAA-ONT-BASIS'] },
  { familyId: 'TAA-ONT-LV-MV', prerequisites: ['TAA-ONT-GEZEGDE'] },
  { familyId: 'TAA-ONT-INVERSIE', prerequisites: ['TAA-ONT-BASIS'] },
  { familyId: 'TAA-ONT-NAAMWOORDELIJK', prerequisites: ['TAA-ONT-GEZEGDE'] },
  { familyId: 'TAA-ONT-HOOFDZIN', prerequisites: ['TAA-ONT-LV-MV'] },
  { familyId: 'TAA-ONT-BIJZIN', prerequisites: ['TAA-ONT-HOOFDZIN'] },
  { familyId: 'TAA-ONT-BWB', prerequisites: ['TAA-ONT-LV-MV'] },
  { familyId: 'TAA-ONT-BETREKKELIJK', prerequisites: ['TAA-ONT-BIJZIN'] },
  { familyId: 'TAA-SOORT-WW', prerequisites: ['TAA-SOORT-LID'] },
  { familyId: 'TAA-SOORT-BNW', prerequisites: ['TAA-SOORT-LID'] },
  { familyId: 'TAA-SOORT-BW', prerequisites: ['TAA-SOORT-BNW'] },
  { familyId: 'TAA-SOORT-TW', prerequisites: ['TAA-SOORT-LID'] },
  { familyId: 'TAA-SOORT-VZ', prerequisites: ['TAA-SOORT-BW'] },
  { familyId: 'TAA-SOORT-VNW', prerequisites: ['TAA-SOORT-VZ'] },
  { familyId: 'TAA-SOORT-VW', prerequisites: ['TAA-SOORT-VZ'] },
  { familyId: 'TAA-LEZEN-DETAIL', prerequisites: [] },
  { familyId: 'TAA-TEKSTSOORT', prerequisites: ['TAA-LEZEN-DETAIL'] },
  { familyId: 'TAA-LEZEN-VERW', prerequisites: ['TAA-LEZEN-DETAIL'] },
  { familyId: 'TAA-SIGNAALWOORD', prerequisites: ['TAA-LEZEN-VERW'] },
  { familyId: 'TAA-LEZEN-VOORSPEL', prerequisites: ['TAA-SIGNAALWOORD'] },
  { familyId: 'TAA-LEZEN-KERN', prerequisites: ['TAA-SIGNAALWOORD'] },
  { familyId: 'TAA-LEZEN-SAMENVATTEN', prerequisites: ['TAA-LEZEN-KERN'] },
  { familyId: 'TAA-LEZEN-CONCLUSIE', prerequisites: ['TAA-LEZEN-KERN'] },
  { familyId: 'TAA-LEZEN-STRUCTUUR', prerequisites: ['TAA-LEZEN-KERN'] },
  { familyId: 'TAA-LEZEN-FEIT', prerequisites: ['TAA-LEZEN-CONCLUSIE'] },
  { familyId: 'TAA-LEZEN-DOEL', prerequisites: ['TAA-LEZEN-FEIT'] },
  { familyId: 'TAA-ARGUMENTATIE', prerequisites: ['TAA-LEZEN-STRUCTUUR'] },
  {
    familyId: 'TAA-LEZEN-VERB',
    prerequisites: ['TAA-LEZEN-DOEL', 'TAA-ARGUMENTATIE'],
  },
  { familyId: 'TAA-WOORD-SYNONIEM', prerequisites: [] },
  { familyId: 'TAA-WOORD-ANTONIEM', prerequisites: ['TAA-WOORD-SYNONIEM'] },
  { familyId: 'TAA-WOORD-CONTEXT', prerequisites: ['TAA-WOORD-ANTONIEM'] },
  { familyId: 'TAA-WOORD-IDIOOM', prerequisites: ['TAA-WOORD-CONTEXT'] },
]

// ─── TOPO — Topografie (21+1 families) ───────────────────────────────────────

const TOPO_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'TOPO-NL-BUURLANDEN', prerequisites: ['TOPO-NL-PROV'] },
  { familyId: 'TOPO-NL-STEDEN', prerequisites: ['TOPO-NL-PROV'] },
  { familyId: 'TOPO-NL-LANDSCHAP', prerequisites: ['TOPO-NL-WATER'] },
  { familyId: 'TOPO-NL-EILANDEN', prerequisites: ['TOPO-NL-WATER'] },
  { familyId: 'TOPO-EU-LAND', prerequisites: ['TOPO-NL-BUURLANDEN'] },
  { familyId: 'TOPO-EU-HOOFDST', prerequisites: ['TOPO-EU-LAND'] },
  { familyId: 'TOPO-EU-FYSISCH', prerequisites: ['TOPO-EU-LAND'] },
  { familyId: 'TOPO-EU-ZEEEN', prerequisites: ['TOPO-EU-FYSISCH'] },
  { familyId: 'TOPO-EU-BUURLANDEN', prerequisites: ['TOPO-EU-LAND'] },
  { familyId: 'TOPO-EU-GEBERGTE', prerequisites: ['TOPO-EU-FYSISCH'] },
  { familyId: 'TOPO-EU-RIVIEREN', prerequisites: ['TOPO-EU-FYSISCH'] },
  { familyId: 'TOPO-EU-KLIMAAT', prerequisites: ['TOPO-EU-FYSISCH'] },
  { familyId: 'TOPO-WERELD-CONT', prerequisites: ['TOPO-EU-LAND'] },
  { familyId: 'TOPO-WERELD-OCEANEN', prerequisites: ['TOPO-WERELD-CONT'] },
  { familyId: 'TOPO-WERELD-HOOFDST', prerequisites: ['TOPO-WERELD-CONT'] },
  { familyId: 'TOPO-WERELD-RIVIER', prerequisites: ['TOPO-WERELD-CONT'] },
  { familyId: 'TOPO-WERELD-GEBERGTE', prerequisites: ['TOPO-WERELD-CONT'] },
  { familyId: 'TOPO-WERELD-LANDEN', prerequisites: ['TOPO-WERELD-CONT'] },
  {
    familyId: 'TOPO-WERELD-KLIMAAT',
    prerequisites: ['TOPO-EU-KLIMAAT', 'TOPO-WERELD-CONT'],
  },
  { familyId: 'TOPO-KLIMAAT', prerequisites: ['TOPO-WERELD-KLIMAAT'] },
]

// ─── STU — Studievaardigheden (20 families) ──────────────────────────────────

const STU_PREREQUISITES: PrerequisiteDefinition[] = [
  { familyId: 'STU-ALFABET', prerequisites: ['STU-INHOUDSOPGAVE'] },
  { familyId: 'STU-WOORDENBOEK', prerequisites: ['STU-ALFABET'] },
  { familyId: 'STU-BIBLIOTHEEK', prerequisites: ['STU-WOORDENBOEK'] },
  { familyId: 'STU-INTERNET', prerequisites: ['STU-BIBLIOTHEEK'] },
  { familyId: 'STU-TABEL', prerequisites: ['STU-INHOUDSOPGAVE'] },
  { familyId: 'STU-GRAFIEK', prerequisites: ['STU-TABEL'] },
  { familyId: 'STU-DIAGRAM', prerequisites: ['STU-TABEL'] },
  { familyId: 'STU-STAAF', prerequisites: ['STU-GRAFIEK'] },
  { familyId: 'STU-TIJDLIJN', prerequisites: ['STU-TABEL'] },
  { familyId: 'STU-LIJN', prerequisites: ['STU-STAAF'] },
  { familyId: 'STU-CIRKEL', prerequisites: ['STU-STAAF'] },
  { familyId: 'STU-FLOWCHART', prerequisites: ['STU-DIAGRAM'] },
  { familyId: 'STU-KAART', prerequisites: ['STU-TABEL'] },
  { familyId: 'STU-ROUTE', prerequisites: ['STU-KAART'] },
  { familyId: 'STU-NOTITIES', prerequisites: ['STU-TABEL'] },
  { familyId: 'STU-SAMENVATTEN', prerequisites: ['STU-NOTITIES'] },
  { familyId: 'STU-MINDMAP', prerequisites: ['STU-SAMENVATTEN'] },
  { familyId: 'STU-PLANNEN', prerequisites: ['STU-SAMENVATTEN'] },
  { familyId: 'STU-COMBINEER', prerequisites: ['STU-KAART', 'STU-GRAFIEK'] },
]

// ─── Combined Export ──────────────────────────────────────────────────────────

/**
 * All prerequisite definitions across all modules.
 *
 * Total: ~280 edges defined (of ~400 estimated).
 * Remaining edges need curriculum designer review.
 */
export const ALL_PREREQUISITES: PrerequisiteDefinition[] = [
  ...GET_PREREQUISITES,
  ...MET_PREREQUISITES,
  ...TGL_PREREQUISITES,
  ...SWW_PREREQUISITES,
  ...SPR_PREREQUISITES,
  ...TAA_PREREQUISITES,
  ...TOPO_PREREQUISITES,
  ...STU_PREREQUISITES,
]

/** Module-specific exports for testing */
export {
  GET_PREREQUISITES,
  MET_PREREQUISITES,
  TGL_PREREQUISITES,
  SWW_PREREQUISITES,
  SPR_PREREQUISITES,
  TAA_PREREQUISITES,
  TOPO_PREREQUISITES,
  STU_PREREQUISITES,
}
